/* eslint-disable react/button-has-type */
import React, {
	useMemo,
	ReactNode,
	forwardRef,
	Ref,
	ButtonHTMLAttributes,
	useCallback,
	MouseEventHandler,
	useId,
	FunctionComponent,
} from 'react';
import { classNames } from 'primereact/utils';
import { universalRenderer } from 'utils/universalRenderer';
import { TooltipProps } from 'primereact/tooltip';
import { Icon, IconProps, IconSet } from '../Icon';
import { Tooltip } from '../Tooltip/Tooltip';
import './Button.scss';

export type ButtonVariantSize = 'xs' | 'sm' | 'md' | 'lg';

export type ButtonVariant =
	| 'primary'
	| 'primary-outlined'
	| 'secondary-outlined'
	| 'priamry-light'
	| 'primary-light-outlined'
	| 'primary-text'
	| 'white-text'
	| 'grey'
	| 'grey-light'
	| 'grey-text'
	| 'rose'
	| 'rose-light'
	| 'rose-text'
	| 'transparent'
	| 'danger'
	| 'danger-text'
	| 'success-text'
	| 'on-purple'
	| 'gray-success'
	| 'gray-primary'
	| 'gray-primary-solid'
	| 'new-purple'
	| 'new-gray';

export type ButtonProps = Pick<
	ButtonHTMLAttributes<HTMLButtonElement>,
	'id' | 'title' | 'className' | 'form' | 'onClick' | 'onMouseDown' | 'onMouseEnter' | 'onMouseLeave' | 'style'
> & {
	label: string;
	active?: boolean;
	loading?: boolean;
	variant?: ButtonVariant;
	variantSize?: ButtonVariantSize;
	noPad?: boolean;
	icon?: string | ReactNode | (() => ReactNode) | FunctionComponent<any>;
	iconSet?: IconSet;
	iconStyle?: IconProps['iconStyle'];
	iconOnly?: boolean;
	iconOnlyOnMobile?: boolean;
	iconClassName?: string;
	iconContainerClassName?: string;
	iconPosition?: 'left' | 'right';
	submit?: boolean;
	unactive?: boolean;
	disabled?: boolean;
	rounded?: boolean;
	circled?: boolean;
	noTitle?: boolean;
	bold?: boolean;
	children?: ReactNode;
	badge?: number;
	block?: boolean;
	responsiveClassName?: string;
	tooltip?: string;
	tooltipPosition?: TooltipProps['position'];
	tooltipClassName?: string;
	renderBefore?: ReactNode | (() => ReactNode);
	renderAfter?: ReactNode | (() => ReactNode);
};

export const Button = forwardRef(
	(
		{
			label,
			active = false,
			loading = false,
			variant = 'primary',
			variantSize = 'md',
			noPad = false,
			className,
			icon,
			iconSet,
			iconStyle,
			iconOnly,
			iconOnlyOnMobile = false,
			iconClassName,
			iconContainerClassName,
			iconPosition = 'left',
			submit = false,
			unactive = false,
			disabled = false,
			rounded = false,
			circled = false,
			noTitle = false,
			bold = false,
			onClick,
			children,
			badge,
			block,
			responsiveClassName,
			style,
			tooltip,
			tooltipPosition = 'top',
			tooltipClassName,
			renderBefore,
			renderAfter,
			...props
		}: ButtonProps,
		ref: Ref<HTMLButtonElement>
	) => {
		const finalClassName = useMemo(() => {
			const variantSizeClassName = classNames({
				'button-size-xs': variantSize === 'xs',
				'button-size-sm': variantSize === 'sm',
				'button-size-md': variantSize === 'md',
				'button-size-lg': variantSize === 'lg',
			});

			const variantClassName = classNames('button', `button-${variant}`, {
				'button-active': active,
				'button-unactive': unactive,
				'button-disabled': disabled,
				'button-rounded': rounded,
				'button-circled': circled,
				'button-icononly': iconOnly,
				'button-icononly-mobile': iconOnlyOnMobile,
				'button-bold': bold,
				'button-block': block,
			});

			return classNames(
				{
					flex: !responsiveClassName,
					[String(responsiveClassName)]: Boolean(responsiveClassName),
				},
				'flex-row',
				variantClassName,
				variantSizeClassName,
				{ 'button-nopad': noPad },
				className
			);
		}, [
			variantSize,
			variant,
			active,
			unactive,
			disabled,
			rounded,
			circled,
			iconOnly,
			iconOnlyOnMobile,
			bold,
			block,
			responsiveClassName,
			noPad,
			className,
		]);

		const renderIcon = () => {
			const renderIconInner = () => {
				if (loading) {
					return <Icon name="circle-notch" iconSet="fa" iconStyle="solid" className="fa-spin" />;
				}
				if (icon) {
					if (typeof icon === 'string') {
						return (
							<Icon
								name={icon}
								iconSet={iconSet}
								iconStyle={iconStyle}
								className={classNames(iconClassName)}
							/>
						);
					}
					return <span className={iconClassName}>{universalRenderer(icon)}</span>;
				}
				return null;
			};
			if (loading || icon || iconOnly) {
				if (iconOnly) {
					return (
						<>
							<span className="button-icon-placeholder" />
							<span className={classNames('button-icon', iconContainerClassName)}>
								{renderIconInner()}
							</span>
						</>
					);
				}
				return <span className={classNames('button-icon', iconContainerClassName)}>{renderIconInner()}</span>;
			}
			return null;
		};

		const title = iconOnly || iconOnlyOnMobile ? label : undefined;

		const renderContent = () => (
			<>
				{renderBefore ? universalRenderer(renderBefore) : null}
				{iconPosition === 'left' && renderIcon()}
				{!iconOnly ? <span className="button-label">{label}</span> : null}
				{iconPosition === 'right' && renderIcon()}
				{renderAfter ? universalRenderer(renderAfter) : null}
				{badge && badge > 0 ? <span className="button-badge bg-rose">{badge}</span> : null}
			</>
		);

		const handleClick: MouseEventHandler<HTMLButtonElement> = useCallback(
			(event) => {
				if (loading || disabled) {
					event.preventDefault();
				} else {
					onClick?.(event);
				}
			},
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[disabled, loading, onClick]
		);

		const generatedId = useId();
		const internalId = props.id ? props.id : `btn-${generatedId.replaceAll(':', '')}`;

		if (tooltip) {
			return (
				<>
					<Tooltip
						content={tooltip}
						target={`#${internalId}-container`}
						position={tooltipPosition}
						className={tooltipClassName}
					/>
					<span
						id={`${internalId}-container`}
						className={classNames('button-container', { 'button-container-block': block })}
					>
						<button
							ref={ref}
							{...(submit ? { type: 'submit' } : {})}
							{...(!submit ? { type: 'button' } : {})}
							className={finalClassName}
							aria-label={label}
							aria-busy={loading}
							disabled={disabled}
							aria-disabled={disabled}
							title={noTitle ? undefined : title}
							onClick={handleClick}
							style={style}
							{...props}
							id={internalId}
						>
							{children || renderContent()}
						</button>
					</span>
				</>
			);
		}

		return (
			<button
				ref={ref}
				{...(submit ? { type: 'submit' } : {})}
				{...(!submit ? { type: 'button' } : {})}
				className={finalClassName}
				aria-label={label}
				aria-busy={loading}
				disabled={disabled}
				aria-disabled={disabled}
				title={noTitle ? undefined : title}
				onClick={handleClick}
				style={style}
				{...props}
				id={internalId}
			>
				{children || renderContent()}
			</button>
		);
	}
);
